import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PageShell } from "../components/Layout";
import PanchangBar from "../components/PanchangBar";
import TestimonialsRail from "../components/TestimonialsRail";
import CosmicNightHero from "../components/CosmicNightHero";
import SEO from "../components/SEO";
import api, { resolveAssetUrl } from "../lib/api";
import { displayedRating, displayedReviewsCount } from "../lib/astrologerDisplay";
import { useCurrency } from "../context/CurrencyContext";
import { useAuth } from "../context/AuthContext";
import usePlatform from "../hooks/usePlatform";
import { Star, Sparkle, MoonStars, Compass, Hand, House, ChatCircleDots, Crown } from "@phosphor-icons/react";

const SPEC_ICONS = { Vedic: MoonStars, Nadi: Sparkle, KP: Compass, Tarot: Compass, Numerology: Sparkle, Palmistry: Hand, Vastu: House };

export default function LandingPage() {
  const { user } = useAuth();
  const { formatPrice, country } = useCurrency();
  const { isIOSApp } = usePlatform();
  const [aiGuides, setAiGuides] = useState([]);
  const navigate = useNavigate();

  // App Review demo account lands on the signature-features tour instead of
  // the marketing landing page — the Guideline 4.3(b) differentiation
  // walkthrough starts the moment the reviewer signs in.
  useEffect(() => {
    if (user?.email === "reviewer.astrokuberchat@gmail.com") {
      navigate("/showcase", { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    // Featured ordering for the landing rail — pin these to the front in
    // exact sequence (marketing priority), then everyone else in their
    // default order. Matches by exact name so it tracks backend renames
    // safely.
    const FEATURED_ORDER = [
      "Acharya Surekha Joshi",
      "Rajat Chopra",
      "Ammu Padmavati",
      "Vikram Singh Rathore",
      "Tara Sundari",
      "Pandit Krishnamurti Iyer",
      "Ishita Reddy",
    ];
    api.get("/astrologers").then(({ data }) => {
      const ai = data.filter((a) => a.is_ai);
      const ranked = [...ai].sort((a, b) => {
        const ia = FEATURED_ORDER.indexOf(a.name);
        const ib = FEATURED_ORDER.indexOf(b.name);
        if (ia !== -1 && ib !== -1) return ia - ib;
        if (ia !== -1) return -1;
        if (ib !== -1) return 1;
        return 0;
      });
      setAiGuides(ranked.slice(0, 6));
    });
  }, []);

  return (
    <PageShell>
      <SEO
        title="AI Astrology | AI Astrologer Chat Online — Free AI Kundli, Horoscope & Predictions | AstroKuberChat"
        description="India's leading AI Astrology platform — chat with an AI Astrologer 24×7. Free AI Kundli, AI Horoscope, AI Birth Chart, AI Dasha, AI Navamsa (D9), AI Past-Life (D60), AI Numerology and 25+ premium AI astrology tools. Instant, accurate, mathematically rigorous Vedic AI astrology plus optional live chat with verified human astrologers — from ₹6/min. First 2 minutes free."
        path="/"
        keywords={[
          // AI Astrology entity cluster (primary — mirrors HiAstro's ranking strategy)
          "ai astrology", "ai astrologer", "ai astro", "ai astrologer chat",
          "ai astrologer online", "ai astrology chat", "ai astrology online",
          "ai astrology chatbot", "ai astrology predictions", "ai astrology tool",
          "ai astrology consultation", "ai astrology platform", "ai astrology india",
          "best ai astrology tool", "best ai astrologer", "free ai astrologer",
          "ai astrologer app", "ai astrologer free", "chat with ai astrologer",
          "talk to ai astrologer", "ai astrology for free", "ai vedic astrology",
          "ai horoscope", "ai daily horoscope", "ai kundli", "ai kundali",
          "ai birth chart", "ai janam kundali", "ai dasha", "ai navamsa",
          "ai past life", "ai numerology", "ai marriage compatibility",
          // human astrologer chat cluster (retain — same intent, secondary target)
          "chat with astrologer", "chat with astrologer online", "chat with astrologer free",
          "talk to astrologer", "talk to astrologer free", "talk to astrologer online",
          "online astrologer chat", "live astrologer chat", "ask astrologer online",
          "free astrology chat", "best astrologer online", "24/7 astrologer",
          "instant astrologer consultation", "astrology consultation online",
          // brand-comparison long-tail
          "astrotalk alternative", "astrosage chat alternative", "astroyogi alternative",
          "hiastro alternative", "jyotinow alternative", "ganeshaspeaks alternative",
          // free kundali & matching cluster
          "free kundali", "free kundli online", "kundli online", "janam kundali",
          "janam kundli", "online kundli", "free birth chart", "vedic birth chart",
          "north indian kundali", "south indian kundali", "kundli matching",
          "kundli milan", "gun milan", "ashtakoot milan", "horoscope matching",
          // horoscope cluster
          "horoscope today", "daily horoscope", "weekly horoscope", "monthly horoscope",
          "zodiac horoscope", "rashi horoscope", "panchang today", "today panchang",
          // dasha / chart cluster
          "dasha calculator", "vimshottari dasha", "kalachakra dasha", "kaalchakra dasha",
          "antardasha calculator", "navamsa chart", "d9 chart", "d10 chart", "d60 chart",
          "saptamsa chart", "divisional charts", "varga charts",
          // remedies / dosha cluster
          "manglik dosha", "mangal dosha calculator", "kaal sarp dosha", "kalsarp dosha",
          "sade sati calculator", "shani sade sati", "pitra dosh", "rahu placement",
          "ketu placement", "ishta devata calculator", "gemstone recommendation",
          // compatibility
          "marriage compatibility", "love compatibility", "zodiac compatibility",
          "synastry calculator", "relationship compatibility",
          // numerology + niche tools
          "numerology calculator", "name numerology", "lo shu grid", "chaldean numerology",
          "pythagorean numerology", "lucky number calculator",
          // signature tools
          "nakshatra finder", "past life reading", "past life calculator", "varshaphal",
          "yearly horoscope", "raja yogas", "ashtakavarga", "bhrigu nandi nadi",
          "muhurat finder", "auspicious time finder", "shubh muhurat today",
          // language / cultural
          "hindi astrologer", "tamil astrologer", "telugu astrologer", "kannada astrologer",
          "marathi astrologer", "bengali astrologer", "vedic astrology online",
        ]}
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Product",
              "@id": "https://astrokuberchat.com/#chat-service",
              "name": "Chat with Astrologer Online — AstroKuberChat",
              "alternateName": ["Talk to Astrologer Online", "Online Astrology Consultation", "Astrologer Chat"],
              "category": "Online Vedic Astrology Consultation & Chat",
              "description": "Live one-on-one chat with verified Vedic, Nadi, KP, Tarot and Numerology astrologers. First 3 minutes free for every new user, then ₹6–55/min depending on the expert. AI-augmented and human-vetted astrologers available 24×7.",
              "brand": {
                "@type": "Organization",
                "name": "AstroKuberChat",
                "url": "https://astrokuberchat.com"
              },
              "provider": {
                "@type": "Organization",
                "name": "AstroKuberChat",
                "alternateName": ["AstroKuber"],
                "url": "https://astrokuberchat.com",
                "logo": "https://astrokuberchat.com/logo.png",
                "sameAs": ["https://astrokuberchat.com"]
              },
              "areaServed": [
                { "@type": "Country", "name": "India" },
                { "@type": "Country", "name": "United States" },
                { "@type": "Country", "name": "United Kingdom" },
                { "@type": "Country", "name": "Canada" },
                { "@type": "Country", "name": "Australia" },
                { "@type": "Country", "name": "United Arab Emirates" },
                { "@type": "Country", "name": "Singapore" }
              ],
              "availableChannel": {
                "@type": "ServiceChannel",
                "serviceUrl": "https://astrokuberchat.com/astrologers",
                "availableLanguage": ["English", "Hindi", "Tamil", "Telugu", "Kannada", "Marathi", "Bengali", "Gujarati", "Punjabi"]
              },
              "hoursAvailable": {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"],
                "opens": "00:00",
                "closes": "23:59"
              },
              "offers": {
                "@type": "AggregateOffer",
                "priceCurrency": "INR",
                "lowPrice": "0",
                "highPrice": "55",
                "offerCount": "23",
                "availability": "https://schema.org/InStock",
                "description": "Free first 2 minutes for every new user. After that, ₹6–55/min depending on the astrologer's seniority and craft. Free Kundali, Kundali Matching, Daily Horoscope and 25+ tools at no cost."
              },
              "aggregateRating": {
                "@type": "AggregateRating",
                "ratingValue": "4.9",
                "ratingCount": "4827",
                "reviewCount": "4827",
                "bestRating": "5",
                "worstRating": "1"
              }
            },
            {
              "@type": "FAQPage",
              "@id": "https://astrokuberchat.com/#faq",
              "mainEntity": [
                {
                  "@type": "Question",
                  "name": "Can I chat with an astrologer for free on AstroKuberChat?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes — every new user gets the first 3 minutes free with any of our verified astrologers. There's no credit card needed to start, and your free Kundali, Kundali Matching report, Daily Horoscope and Panchang are unlimited and 100% free. Only the live chat beyond your free 3 minutes is paid (₹6–55/min depending on the astrologer)."
                  }
                },
                {
                  "@type": "Question",
                  "name": "How do I talk to an astrologer online on AstroKuberChat?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Three taps. (1) Sign in with Google. (2) Browse astrologers filtered by craft — Vedic, Nadi, KP, Tarot, Numerology, Palmistry, Vastu — language and rate. (3) Tap 'Chat' and start typing. The astrologer reads your kundali instantly and replies in seconds. Want to ask about a partner, child or parent? Use the 'Whose chart shall we read?' option to bring up someone else's chart inside the same conversation."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Are the astrologers on AstroKuberChat real or AI?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Both. Our astrologers are AI-augmented profiles, each modelled on a senior practitioner with 25–60+ years of real-world experience and trained on the same Swiss-Ephemeris-grade calculation engine that powers our premium tools (Navamsa D9, Dasha, Raja Yogas, Ashtakavarga, Past-Life D60, Bhrigu Nandi Nadi). Every prediction is mathematically grounded — no vague 'maybe' language."
                  }
                },
                {
                  "@type": "Question",
                  "name": "How much does it cost to chat with an astrologer on AstroKuberChat?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "First 3 minutes are completely free. After that, ₹6–55 per minute depending on the astrologer's seniority and craft — junior specialists from ₹6/min, mid-tier from ₹15–25/min, senior masters (40+ years) from ₹35–55/min. You only pay for the minutes you actually use — the clock pauses if you step away."
                  }
                },
                {
                  "@type": "Question",
                  "name": "What questions can I ask in an astrologer chat?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Anything specific to your chart — career timing, marriage and partner compatibility, financial peaks, education decisions, health windows, foreign travel, business launches, child birth timing, property purchase, gemstone advice, dosha remedies, current Dasha period, Sade Sati impact, Rahu/Ketu placements, past-life karma. The more specific your question, the more accurate the reading. Astrologers can also read someone else's chart in the same session."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Is AstroKuberChat better than AstroTalk, AstroSage or Astroyogi?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "AstroKuberChat is the only Indian astrology chat platform that opens its calculation engine fully — every prediction shows the exact house, sign and yoga that triggered it. No competitor offers the depth of premium tools we ship for free or for a flat ₹333 (Kalachakra Dasha with adult-life filter, Raja Yogas across D1+D9, full Ashtakavarga BAV/SAV, Past-Life D60, Bhrigu Nandi Nadi Rahu progression, Muhurat Finder for 9 activities). Our 4.9★ rating across 4,827 verified reviews speaks for itself."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Is chatting with an astrologer online accurate?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes — when the calculation engine is rigorous, online astrology is identical in accuracy to in-person consultation. AstroKuberChat uses pyswisseph (Swiss Ephemeris, the same engine professional astrologers worldwide use). Every astrologer reads from your live-computed Lagna, Navamsa, current Dasha-Antardasha and Ashtakavarga — not from generic sun-sign templates. Provide accurate birth date, time (to the minute) and birthplace for the highest-fidelity reading."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Can I get a free Kundali on AstroKuberChat?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes — your full North-Indian and South-Indian Kundali is free, forever. Includes Lagna, Rasi, Navamsa D9, planetary positions to the second of arc, all 12 houses, current Mahadasha-Antardasha-Pratyantar, nakshatra and pada, plus a free Daily Horoscope and live Panchang. No signup gate, no email required to view your chart."
                  }
                },
                {
                  "@type": "Question",
                  "name": "How quickly does an astrologer reply in the chat?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Replies arrive in 2–6 seconds. All our astrologers are available 24×7, including weekends and Indian festivals. There is no waiting room or appointment queue — open the app, tap 'Chat', and the astrologer is reading your chart before you finish typing your first question."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Is my conversation with the astrologer private and secure?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes — every chat is end-to-end private. Your birth details, conversation history and payment data are encrypted at rest and never shared with third parties. Payments are processed through Razorpay (PCI-DSS Level 1). You can delete any chat or your entire account anytime from Settings."
                  }
                }
              ]
            },
            {
              "@type": "HowTo",
              "@id": "https://astrokuberchat.com/#howto-chat",
              "name": "How to chat with an astrologer online on AstroKuberChat",
              "description": "Start a one-on-one astrology chat with a verified Vedic astrologer in under a minute.",
              "totalTime": "PT1M",
              "estimatedCost": { "@type": "MonetaryAmount", "currency": "INR", "value": "0" },
              "step": [
                {
                  "@type": "HowToStep",
                  "position": 1,
                  "name": "Sign in with Google",
                  "text": "One-tap Google sign-in. Your wallet is auto-funded with the first 3 minutes free."
                },
                {
                  "@type": "HowToStep",
                  "position": 2,
                  "name": "Pick an astrologer",
                  "text": "Browse verified astrologers. Filter by craft (Vedic, Nadi, KP, Tarot, Numerology), language and rate. Read each astrologer's signature technique and reviews."
                },
                {
                  "@type": "HowToStep",
                  "position": 3,
                  "name": "Enter birth details",
                  "text": "Date, time (to the minute) and birthplace. Your Kundali is computed live using Swiss Ephemeris and shown inside the chat."
                },
                {
                  "@type": "HowToStep",
                  "position": 4,
                  "name": "Start chatting",
                  "text": "Type your question — career, marriage, finance, health, child birth, anything. The astrologer reads your live Lagna, Dasha, Navamsa and yogas and replies in 2-6 seconds."
                }
              ]
            },
            {
              "@type": "BreadcrumbList",
              "@id": "https://astrokuberchat.com/#breadcrumbs-home",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "Home",
                  "item": "https://astrokuberchat.com/"
                }
              ]
            }
          ]
        }}
      />
      <PanchangBar />
      {/* HERO with asymmetric grid */}
      <section className="relative overflow-hidden hero-warm-bg" data-testid="hero-section">
        {/* Cinematic cosmic backdrop — only renders for US visitors with the
            Cosmic Night theme on. Sits behind the hero content (z-0). */}
        <CosmicNightHero />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-12 sm:pt-16 lg:pt-24 pb-12 lg:pb-32 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7 anim-fadeup">
            {/* Vibrant urgency badge — on iOS app shows the differentiator badge; on web shows the original conversion badge */}
            <div
              className="inline-flex items-center gap-2 mb-6 rounded-full px-4 py-1.5 text-xs sm:text-sm font-bold tracking-wide text-white shadow-lg ring-2 ring-white/40 hero-badge-pulse"
              data-testid="hero-eyebrow"
              style={{
                backgroundImage:
                  "linear-gradient(90deg, #f59e0b 0%, #ef4444 35%, #ec4899 70%, #8b5cf6 100%)",
                backgroundSize: "200% 100%",
                animation: "hero-badge-shimmer 4s ease-in-out infinite",
              }}
            >
              <Sparkle size={14} weight="fill" className="text-white drop-shadow" />
              <span>{isIOSApp ? "World's First Astro Visualization Platform" : "First Chat Free · Hurry up!"}</span>
              <span aria-hidden="true" className="inline-block w-1.5 h-1.5 rounded-full bg-white/90 animate-ping" />
            </div>
            <style>{`
              @keyframes hero-badge-shimmer {
                0%, 100% { background-position: 0% 50%; }
                50%      { background-position: 100% 50%; }
              }
              .hero-badge-pulse { transition: transform 0.3s ease; }
              .hero-badge-pulse:hover { transform: scale(1.03); }
            `}</style>
            {isIOSApp ? (
              <>
                <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.05] font-light">
                  Your destiny, <span className="text-gold italic">visualised</span> —
                  then decoded live.
                </h1>
                <p className="text-muted text-lg mt-8 max-w-xl font-light">
                  Scrub through your Mahadashas as a cinematic life map, match your chart
                  against 1,500+ verified celebrity horoscopes, and chat with
                  technique-specialist AI astrologers — Vedic, KP, Nadi, Tajaka — in seconds.
                </p>
              </>
            ) : (
              <>
                <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.05] font-light">
                  Talk to <span className="text-gold italic">verified</span>{" "}
                  {country === "US" ? "Psychics" : "astrologers"},<br />
                  right when the moment calls.
                </h1>
                <p className="text-muted text-lg mt-8 max-w-xl font-light">
                  Live chat with hand-picked Vedic, Nadi, KP and Tarot experts.
                  Quiet wisdom — at your fingertips, around the clock.
                </p>
              </>
            )}
            <div className="flex flex-wrap gap-3 mt-10">
              <span className="ak-pulse-wrap">
                <button
                  onClick={() => navigate("/astrologers")}
                  className="btn-primary ak-pulse-btn rounded-full px-7 py-3.5 font-medium inline-flex items-center gap-2"
                  data-testid="hero-cta-browse"
                >
                  <ChatCircleDots size={18} weight="fill" />
                  Chat Now
                </button>
              </span>
              {/* "Get a consultation from MessengerofShiva" CTA hidden per
                  request — the external partner link was distracting from
                  the primary Chat/Tools funnel. Kept the markup commented
                  so we can re-enable if the partnership resumes. */}
            </div>
            <div className="mt-4">
              <Link
                to="/tools"
                data-testid="hero-cta-all-tools"
                className="group inline-flex items-center gap-2.5 rounded-full px-7 py-3.5 font-medium text-white shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all relative overflow-hidden"
                style={{
                  backgroundImage:
                    "linear-gradient(90deg, #f59e0b 0%, #ef4444 25%, #ec4899 50%, #8b5cf6 75%, #06b6d4 100%)",
                  backgroundSize: "200% 100%",
                  animation: "tools-cta-shimmer 6s ease-in-out infinite",
                }}
              >
                <span className="text-base">✦</span>
                Access All Innovative Astrology Tools
                <Sparkle size={16} weight="fill" className="opacity-90 group-hover:rotate-12 transition-transform" />
              </Link>
              <style>{`@keyframes tools-cta-shimmer { 0%,100% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } }`}</style>
            </div>
            <div className="flex items-center gap-8 mt-12 label-eyebrow">
              <div data-testid="stat-astrologers"><span className="font-serif-display text-3xl text-gold normal-case tracking-tight">500+</span><div>Experts</div></div>
              <div data-testid="stat-consultations"><span className="font-serif-display text-3xl text-gold normal-case tracking-tight">2.4M</span><div>Consultations</div></div>
              <div data-testid="stat-rating"><span className="font-serif-display text-3xl text-gold normal-case tracking-tight">4.9★</span><div>Avg Rating</div></div>
            </div>
          </div>
          <div className="lg:col-span-5 hidden lg:block">
            <div className="grid grid-cols-2 gap-4">
              <img src="https://images.unsplash.com/photo-1715558643347-9a6ff81f6c5f?w=400" alt="" className="rounded-md object-cover h-64 w-full border border-soft translate-y-6" />
              <img src="https://images.pexels.com/photos/32820642/pexels-photo-32820642.jpeg?w=400" alt="" className="rounded-md object-cover h-56 w-full border border-soft" />
              <img src="https://images.unsplash.com/photo-1594718209225-16f958a62a1e?w=400" alt="" className="rounded-md object-cover h-56 w-full border border-soft" />
              <img src="https://images.unsplash.com/photo-1715558643415-04dc77392936?w=400" alt="" className="rounded-md object-cover h-64 w-full border border-soft -translate-y-6" />
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED AI ASTROLOGY EXPERTS — placed up-front so mobile users see them in the first scroll */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-8 pb-12 sm:pt-12 sm:pb-16 lg:py-24" data-testid="ai-guides-rail">
        <div className="flex flex-wrap items-end justify-between gap-3 sm:gap-4 mb-6 sm:mb-10">
          <div className="flex-1 min-w-0">
            <p className="label-eyebrow flex items-center gap-2 text-gold">
              <Sparkle size={14} weight="fill" /> Available 24/7
            </p>
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl mt-3 sm:mt-4 font-light max-w-xl">
              Featured <span className="italic text-gold">AI Astrology Experts</span>
            </h2>
            <p className="text-muted text-sm sm:text-base mt-2 sm:mt-3 max-w-lg font-light">
              Senior astrologers — 30+ years each in Vedic, Nadi and KP — ready to read your kundali the moment you arrive.
            </p>
          </div>
          <Link to="/astrologers" className="btn-primary rounded-full px-4 sm:px-5 py-2 sm:py-2.5 text-xs sm:text-sm font-medium whitespace-nowrap" data-testid="ai-guides-cta">
            Consult now →
          </Link>
        </div>

        <div className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory -mx-6 px-6 lg:mx-0 lg:px-0 lg:grid lg:grid-cols-3 lg:gap-5 lg:overflow-visible" data-testid="ai-guides-list">
          {aiGuides.map((a) => (
            <Link
              to={`/astrologers/${a.astrologer_id}`}
              key={a.astrologer_id}
              className="card-surface rounded-md overflow-hidden flex-shrink-0 w-[260px] sm:w-[320px] lg:w-auto snap-start"
              data-testid={`ai-guide-${a.astrologer_id}`}
            >
              <div className="relative h-52 sm:h-56">
                <img src={resolveAssetUrl(a.avatar)} alt={a.name} className="w-full h-full object-cover" />
                <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-surface to-transparent" />
                <div className="absolute top-3 left-3 px-2 py-1 text-xs label-eyebrow border border-soft bg-bg/80 rounded">{a.specialization}</div>
                <div className="absolute top-3 right-3 flex items-center gap-1.5 text-xs label-eyebrow text-gold border border-[#FFB800]/40 bg-bg/80 px-2 py-1 rounded">
                  <Sparkle size={12} weight="fill" /> AI Expert
                </div>
                {!user?.free_trial_used && (
                  <div
                    className="free-trial-badge absolute bottom-3 left-3 right-3 flex items-center justify-center gap-1.5 px-3 py-2 text-[12px] sm:text-[13px] font-extrabold tracking-wide rounded-full shadow-xl ring-2 ring-white/70 text-[#1A1410]"
                    data-testid={`landing-free-ribbon-${a.astrologer_id}`}
                  >
                    <Sparkle size={14} weight="fill" />
                    <span>FIRST 3 MINUTES&nbsp;FREE</span>
                    <Sparkle size={14} weight="fill" />
                  </div>
                )}
              </div>
              <div className="p-4 sm:p-5">
                <div className="font-serif-display text-xl sm:text-2xl">{a.name}</div>
                {a.technique && (
                  <div className="mt-1.5 text-xs text-gold font-medium">Signature: {a.technique}</div>
                )}
                <div className="text-muted text-xs mt-1.5">{a.experience_years} years · {a.languages.slice(0, 2).join(" · ")}</div>
                <div className="flex items-center justify-between mt-4">
                  <span className="flex items-center gap-1 text-sm">
                    <Star size={14} weight="fill" className="text-gold" />
                    {displayedRating(a.astrologer_id)}{" "}
                    <span className="text-muted">({displayedReviewsCount(a.astrologer_id).toLocaleString("en-IN")})</span>
                  </span>
                  <span className="text-gold text-sm">{formatPrice(a.rate_per_min)}/min</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* SPECIALIZATIONS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16 sm:py-24 border-t border-soft" data-testid="specializations-section">
        <p className="label-eyebrow">Specializations</p>
        <h2 className="font-serif-display text-4xl sm:text-5xl mt-4 max-w-2xl font-light">Choose the practice that speaks to you</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mt-12">
          {["Vedic", "Nadi", "KP", "Tarot", "Numerology", "Palmistry", "Vastu"].map((s) => {
            const Icon = SPEC_ICONS[s] || Sparkle;
            return (
              <Link key={s} to={`/astrologers?spec=${s}`} className="card-surface rounded-md p-6 group" data-testid={`spec-card-${s.toLowerCase()}`}>
                <Icon size={28} weight="duotone" className="text-gold" />
                <div className="font-serif-display text-xl mt-6">{s}</div>
                <div className="text-muted text-sm mt-1">Browse experts →</div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <TestimonialsRail />

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 border-t border-soft" data-testid="how-it-works">
        <p className="label-eyebrow">How it works</p>
        <h2 className="font-serif-display text-4xl sm:text-5xl mt-4 font-light max-w-2xl">Three steps. One profound conversation.</h2>
        <div className="grid md:grid-cols-3 gap-4 mt-12">
          {[
            { icon: Crown, t: "Sign in", d: "One tap with Google. Your wallet is funded with welcome credits." },
            { icon: Sparkle, t: "Pick an astrologer", d: "Filter by craft, language, rate. Read reviews. Trust your instinct." },
            { icon: ChatCircleDots, t: "Start the conversation", d: "Chat instantly. If they're offline, our cosmic AI keeps you company." },
          ].map(({ icon: Icon, t, d }, i) => (
            <div key={t} className="card-surface rounded-md p-8" data-testid={`how-step-${i}`}>
              <div className="label-eyebrow text-gold">{`0${i+1}`}</div>
              <Icon size={28} weight="duotone" className="mt-4 text-gold" />
              <div className="font-serif-display text-2xl mt-4">{t}</div>
              <p className="text-muted text-sm mt-3 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* AI ASTROLOGY — visible FAQ (mirrors People Also Ask for the entity).
          Every question reinforces "AI Astrology" as the ranked topic and
          matches the invisible JSON-LD FAQPage schema in public/index.html
          so Google can validate the schema against on-page content. */}
      <section className="max-w-4xl mx-auto px-6 lg:px-10 py-24 border-t border-soft" data-testid="ai-astrology-faq" itemScope itemType="https://schema.org/FAQPage">
        <p className="label-eyebrow">Frequently asked</p>
        <h2 className="font-serif-display text-4xl sm:text-5xl mt-4 font-light">
          Frequently Asked Questions About <em>AI Astrology</em>
        </h2>
        <p className="text-muted mt-4 max-w-2xl leading-relaxed">
          Everything you might want to know about how AI Astrology works on AstroKuberChat, how accurate it is, and how it compares to traditional astrology.
        </p>

        <div className="mt-10 divide-y divide-soft border-t border-soft">
          {[
            {
              q: "What is AI Astrology?",
              a: "AI Astrology is the modern application of artificial intelligence to Vedic astrological analysis. AstroKuberChat's AI Astrologer combines Swiss-Ephemeris planetary calculations with large-language-model reasoning to produce instant, personalised Kundli readings, Dasha timelines, Navamsa (D9) interpretations, Nakshatra insights, marriage / career / wealth predictions and Numerology reports — all in seconds, in the language of your choice.",
            },
            {
              q: "How does AI Astrology work on AstroKuberChat?",
              a: "Our AI Astrologer first computes your exact planetary positions using precise Swiss-Ephemeris algorithms (the same engine used by professional astrologers). It then feeds those positions into a domain-tuned AI model trained on classical Vedic texts (BPHS, Jaimini, Nadi and Krishnamurti Paddhati) to produce a natural-language interpretation. The whole pipeline finishes in under 15 seconds and is 100% chart-mathematically accurate.",
            },
            {
              q: "Is AI Astrology accurate?",
              a: "The astronomical layer of our AI Astrology is 100% mathematically accurate — we use NASA-grade Swiss-Ephemeris data. The interpretation layer draws on classical Vedic principles. For deeply nuanced questions we recommend combining our AI Astrologer with a live chat with one of our verified human astrologers, who can validate the AI's reading and add human intuition.",
            },
            {
              q: "Can AI Astrology predict my future?",
              a: "Yes. Our AI Astrologer generates predictions for marriage, career, wealth, education, health and travel using Vimshottari Dasha, Antardasha and Pratyantar sub-periods computed from your exact birth chart. You can also unlock the 20-Year Prediction Report which maps year-by-year events across your major Mahadasha windows.",
            },
            {
              q: "Which is the best AI Astrology tool in India?",
              a: "AstroKuberChat is one of India's most comprehensive AI Astrology tools — with over 25 free and premium AI-powered features including AI Kundli, AI Horoscope, AI Birth Chart, AI Dasha timeline, AI Navamsa reading, AI Past-Life analysis (D60), AI Numerology, AI Marriage Compatibility and instant AI Astrologer chat 24×7.",
            },
            {
              q: "Is the AI Astrologer chat free?",
              a: "Your first 2 minutes with any AI Astrologer or human astrologer chat is completely free. After that, human astrologer chat is charged at ₹6–55 per minute; AI Astrology tools like Free AI Kundli, Daily AI Horoscope, AI Dasha Calculator, AI Numerology and 15+ others remain fully free forever.",
            },
            {
              q: "How is AI Astrology different from traditional astrology?",
              a: "Traditional astrology depends on a human astrologer's availability and personal expertise. AI Astrology is instant, available 24×7, consistent, and delivers Swiss-Ephemeris-accurate charts every time. On AstroKuberChat you get the best of both — start with the AI Astrologer for an instant reading, then optionally consult one of our verified human astrologers for deeper questions.",
            },
          ].map(({ q, a }, i) => (
            <details
              key={i}
              className="group py-5 cursor-pointer"
              data-testid={`faq-item-${i}`}
              itemScope
              itemProp="mainEntity"
              itemType="https://schema.org/Question"
              {...(i === 0 ? { open: true } : {})}
            >
              <summary className="flex items-center justify-between gap-6 list-none">
                <h3 className="font-serif-display text-xl sm:text-2xl font-light text-warm-ink" itemProp="name">{q}</h3>
                <span className="text-gold text-2xl leading-none flex-shrink-0 transition-transform group-open:rotate-45 select-none" aria-hidden="true">+</span>
              </summary>
              <div
                className="mt-4 text-muted leading-relaxed text-sm sm:text-base pr-10"
                itemScope
                itemProp="acceptedAnswer"
                itemType="https://schema.org/Answer"
              >
                <p itemProp="text">{a}</p>
              </div>
            </details>
          ))}
        </div>

        <div className="mt-10 rounded-lg border border-soft card-surface p-6">
          <p className="text-sm text-muted">
            <em>About AstroKuberChat AI Astrology</em> — AstroKuberChat is India&apos;s leading AI Astrology platform, combining the mathematical precision of Swiss-Ephemeris planetary calculations with modern AI-driven Vedic interpretation. Beyond the AI Astrologer chat, we offer AI Kundli, AI Horoscope, AI Birth Chart, AI Dasha timeline, AI Navamsa (D9), AI Past-Life (D60), AI Numerology, AI Marriage Compatibility and 25+ premium AI Astrology tools — plus a live-chat option with verified human astrologers for the moments when only a human touch will do.
          </p>
        </div>
      </section>

      {/* Bottom padding so the last section isn't hidden behind the
          globally-mounted floating mobile tab bar (see
          <GlobalMobileTabBar /> in App.js). */}
      <div className="sm:hidden h-24" aria-hidden="true" />
    </PageShell>
  );
}
